// Single source of truth for the careers screening-interview dimensions: the
// screening interviewer gathers evidence per dimension, coverage decides what
// still needs probing, the labels speak to admins in plain words, and the
// weights drive the deterministic weighted-total scoring (Task 4).
// Model: src/lib/z2h/coverage.ts.

export const SCREEN_DIMENSIONS = [
  'role_fit',
  'experience_depth',
  'communication',
  'ownership_signals',
  'logistics',
] as const

export type ScreenDimension = (typeof SCREEN_DIMENSIONS)[number]

// Admin-facing phrasing — used in coverage/progress guidance, never internal ids.
export const SCREEN_LABELS: Record<ScreenDimension, string> = {
  role_fit: 'role fit',
  experience_depth: 'experience depth',
  communication: 'communication',
  ownership_signals: 'ownership signals',
  logistics: 'logistics',
}

// Weighted-total inputs for the deterministic scorer (Task 4) — the model
// never computes a total, only per-dimension scores.
export const SCREEN_WEIGHTS: Record<ScreenDimension, number> = {
  role_fit: 0.3,
  experience_depth: 0.25,
  communication: 0.2,
  ownership_signals: 0.15,
  logistics: 0.1,
}

// Dimensions that still have no recorded evidence — a dimension counts as
// covered once its answers key exists with at least one collected fact.
export function remainingDimensions(
  answers: Record<string, unknown> | null | undefined,
): ScreenDimension[] {
  return SCREEN_DIMENSIONS.filter((d) => {
    const value = answers?.[d]
    if (value === undefined || value === null) return true
    if (typeof value === 'object' && Object.keys(value as object).length === 0) return true
    return false
  })
}

// Plain-words phrasing for a list of dimension ids — used by both the voice
// assistant (system prompt + resume greeting) and the text chat (progress
// copy) so a candidate never sees the internal snake_case id.
export function labelsFor(dimensions: readonly string[]): string[] {
  return dimensions.map((d) => SCREEN_LABELS[d as ScreenDimension] || String(d).replace(/_/g, ' '))
}

// Joins labels into natural-language "and"/", and" phrasing: "a", "a and b",
// or "a, b, and c".
export function listLabels(dimensions: readonly string[]): string {
  const labels = labelsFor(dimensions)
  if (labels.length <= 1) return labels[0] || ''
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}
